(function (global) {
  'use strict';

  var CARD_SELECTOR = 'ooo-wl-page-product-card';
  var PAGE_SELECTOR = 'ooo-wl-page-container';
  var FACTS_SECTION_ID = 'tf-wishlist-product-facts';
  var MAX_CONCURRENT_FACT_REQUESTS = 4;
  var factsCache = new Map();
  var observedRoots = new WeakSet();
  var enhancingStates = new WeakMap();
  var ctaSpacingStates = new WeakMap();
  var availabilityLabels = {
    new: 'NEW',
    bestseller: 'BESTSELLER',
    coming_soon: 'COMING SOON',
    back_soon: 'BACK SOON',
    pre_order: 'PRE-ORDER',
    sold_out: 'SOLD OUT'
  };
  var availabilityBadgeClasses = {
    new: 'badge--new',
    bestseller: 'badge--bestseller',
    coming_soon: 'badge--coming-soon',
    back_soon: 'badge--back-soon',
    pre_order: 'badge--pre-order',
    sold_out: 'badge--sold-out'
  };
  function getCardContext(card) {
    var root = card.shadowRoot;
    var pid = card.getAttribute('pid') || '';
    var pidMatch = pid.match(/^(\d+)-(\d+)$/);
    var productLink;
    var productUrl;
    var variantId;

    if (!root || !pidMatch) {
      return null;
    }

    productLink = root.querySelector('a[href*="/products/"]');

    if (!productLink) {
      return null;
    }

    try {
      productUrl = new URL(productLink.href, global.location.href);
    } catch (error) {
      return null;
    }

    variantId = productUrl.searchParams.get('variant') || pidMatch[2];

    if (!/^\d+$/.test(variantId)) {
      return null;
    }

    return {
      key: pidMatch[1] + '-' + variantId,
      productId: pidMatch[1],
      variantId: variantId,
      productUrl: productUrl,
      productLink: productLink,
      root: root
    };
  }

  function getProductTitleLink(root, productLink) {
    var productLinks = root.querySelectorAll('a[href*="/products/"]');

    if (productLinks.length < 2) {
      return null;
    }

    if (productLinks[0] !== productLink) {
      return null;
    }

    return productLinks[productLinks.length - 1];
  }

  function createFactsUrl(context) {
    var url = new URL(context.productUrl.href);

    url.searchParams.set('variant', context.variantId);
    url.searchParams.set('section_id', FACTS_SECTION_ID);
    url.searchParams.delete('from');

    return url.href;
  }

  function isValidFacts(facts, context) {
    return (
      facts &&
      typeof facts === 'object' &&
      String(facts.productId) === context.productId &&
      String(facts.variantId) === context.variantId &&
      typeof facts.available === 'boolean' &&
      Number.isInteger(facts.priceCents) &&
      facts.priceCents >= 0 &&
      typeof facts.availabilityState === 'string' &&
      facts.presetBundle &&
      typeof facts.presetBundle === 'object' &&
      typeof facts.presetBundle.isPresetBundle === 'boolean' &&
      typeof facts.presetBundle.discountEligible === 'boolean' &&
      (facts.presetBundle.pieceCount === null || (Number.isInteger(facts.presetBundle.pieceCount) && facts.presetBundle.pieceCount >= 2)) &&
      (facts.presetBundle.isPresetBundle || facts.presetBundle.pieceCount === null) &&
      (!facts.presetBundle.isPresetBundle || facts.presetBundle.pieceCount !== null) &&
      (!facts.presetBundle.discountEligible || facts.presetBundle.isPresetBundle)
    );
  }

  function fetchFacts(context) {
    var cached = factsCache.get(context.key);

    if (cached) {
      return cached;
    }

    cached = global.fetch(createFactsUrl(context), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Wishlist product facts request failed');
        }

        return response.text();
      })
      .then(function (html) {
        var documentFragment = new DOMParser().parseFromString(html, 'text/html');
        var factsNode = documentFragment.querySelector('[data-tf-wishlist-product-facts]');
        var facts;

        if (!factsNode) {
          throw new Error('Wishlist product facts were not rendered');
        }

        facts = JSON.parse(factsNode.textContent || '');

        if (!isValidFacts(facts, context)) {
          throw new Error('Wishlist product facts were invalid');
        }

        return facts;
      })
      .catch(function () {
        factsCache.delete(context.key);
        return null;
      });

    factsCache.set(context.key, cached);
    return cached;
  }

  function injectStyles(root) {
    var style;

    if (root.querySelector('[data-tf-wishlist-merchandising-styles]')) {
      return;
    }

    style = global.document.createElement('style');
    style.setAttribute('data-tf-wishlist-merchandising-styles', '');
    style.textContent = '[data-tf-wishlist-image-link] { display: block; position: relative; } [data-tf-wishlist-title] { display: block; min-block-size: 2.8em; } [data-tf-wishlist-availability-badge] { inset-block-start: 10px; inset-inline-start: 10px; position: absolute; z-index: 1; } [data-tf-wishlist-availability-badge] .badge { border: 0; border-radius: 6px; box-shadow: none; font-family: var(--text-font-family); font-size: 9px; font-weight: 600; letter-spacing: .06em; line-height: 1; padding: 6px 9px; } [data-tf-wishlist-availability-badge] .badge--new { background: #eae5dc; color: #5a4931; } [data-tf-wishlist-availability-badge] .badge--bestseller { background: #3d342c; color: #fffdf8; } [data-tf-wishlist-availability-badge] .badge--coming-soon { background: #d5cbb9; color: #4a3c29; } [data-tf-wishlist-availability-badge] .badge--back-soon { background: #c5b89f; color: #3d3428; } [data-tf-wishlist-availability-badge] .badge--pre-order { background: #8b7247; color: #fff; } [data-tf-wishlist-availability-badge] .badge--sold-out { background: #e8c9c6; color: #7a2f2f; } [data-tf-wishlist-merchandising] { display: grid; grid-template-rows: minmax(1.65em, auto) 1.65em; justify-items: center; margin: 0; row-gap: .25rem; text-align: center; } [data-tf-wishlist-price] { align-items: baseline; column-gap: .5rem; display: flex; flex-wrap: wrap; justify-content: center; min-block-size: 1.65em; } [data-tf-wishlist-bundle-price] { align-items: baseline; display: flex; flex-wrap: wrap; gap: 4px 10px; justify-content: center; } [data-tf-wishlist-effective-price] { font-weight: inherit; } [data-tf-wishlist-parent-price] { color: rgb(var(--text-color) / .62); font-size: .875em; text-decoration: line-through; } [data-tf-wishlist-set-info] { color: rgb(var(--text-color) / .78); font-size: var(--text-xs); font-weight: 500; line-height: 1.65; min-block-size: 1.65em; } @media screen and (min-width: 700px) { [data-tf-wishlist-availability-badge] { inset-block-start: 12px; inset-inline-start: 12px; } [data-tf-wishlist-availability-badge] .badge { font-size: 10px; padding: 7px 11px; } }';
    style.textContent += ' [data-tf-wishlist-enhancing-cta] { visibility: hidden; }';
    root.appendChild(style);
  }

  function removeAvailabilityBadge(root, productLink) {
    var badge = root.querySelector('[data-tf-wishlist-availability-badge]');

    if (badge) {
      badge.remove();
    }

    if (productLink) {
      productLink.removeAttribute('data-tf-wishlist-image-link');
    }
  }

  function removeTitleSlot(root) {
    var titleLink = root.querySelector('a[data-tf-wishlist-title]');

    if (titleLink) {
      titleLink.removeAttribute('data-tf-wishlist-title');
    }
  }

  function applyTitleSlot(context) {
    var titleLink = getProductTitleLink(context.root, context.productLink);

    removeTitleSlot(context.root);

    if (titleLink) {
      titleLink.setAttribute('data-tf-wishlist-title', '');
    }

    return titleLink;
  }

  function insertAfterTitle(node, titleLink, root) {
    if (titleLink && titleLink.parentNode) {
      if (titleLink.nextSibling) {
        titleLink.parentNode.insertBefore(node, titleLink.nextSibling);
      } else {
        titleLink.parentNode.appendChild(node);
      }

      return;
    }

    root.appendChild(node);
  }

  function getPurchaseForm(button) {
    if (!button || typeof button.closest !== 'function') {
      return null;
    }

    return button.closest('form');
  }

  function markCtaHierarchy(purchaseForm) {
    if (!purchaseForm) {
      return;
    }

    purchaseForm.setAttribute('data-tf-wishlist-cta-form', '');
  }

  function clearCtaHierarchy(root) {
    var form = root.querySelector('[data-tf-wishlist-cta-form]');

    if (form) {
      restoreCtaPosition(form);
      form.removeAttribute('data-tf-wishlist-cta-form');
    }
  }

  function restoreCtaPosition(form) {
    var state = ctaSpacingStates.get(form);

    if (!state) {
      return;
    }

    form.style.removeProperty('margin-block-start');

    if (state.marginBlockStart) {
      form.style.setProperty('margin-block-start', state.marginBlockStart, state.marginBlockStartPriority);
    }

    ctaSpacingStates.delete(form);
  }

  function positionCtaForm(panel, form) {
    var state;

    if (!panel || !form || typeof panel.getBoundingClientRect !== 'function' || typeof form.getBoundingClientRect !== 'function' || typeof global.getComputedStyle !== 'function') {
      return;
    }

    state = ctaSpacingStates.get(form);

    if (!state) {
      state = {
        marginBlockStart: form.style.getPropertyValue('margin-block-start'),
        marginBlockStartPriority: typeof form.style.getPropertyPriority === 'function' ? form.style.getPropertyPriority('margin-block-start') : ''
      };
      ctaSpacingStates.set(form, state);
    }

    function correctPosition() {
      var panelRect;
      var formRect;
      var currentMargin;
      var renderedGap;
      var targetMargin;

      if (ctaSpacingStates.get(form) !== state) {
        return;
      }

      panelRect = panel.getBoundingClientRect();
      formRect = form.getBoundingClientRect();
      renderedGap = formRect.top - panelRect.bottom;
      currentMargin = parseFloat(global.getComputedStyle(form).marginBlockStart);

      if (!Number.isFinite(renderedGap) || !Number.isFinite(currentMargin)) {
        return;
      }

      targetMargin = currentMargin + (12 - renderedGap);
      form.style.setProperty('margin-block-start', targetMargin + 'px');
    }

    correctPosition();

    if (typeof global.requestAnimationFrame === 'function') {
      global.requestAnimationFrame(correctPosition);
    }
  }

  function beginEnhancing(card, root) {
    var button = root.querySelector('[data-field="form-button"]');
    var styleValue = card.style.getPropertyValue ? card.style.getPropertyValue('--ooo-wl-page-product-card-price-display') : '';
    var state = enhancingStates.get(card);

    if (state) {
      if (!state.button && button) {
        state.button = button;
        state.buttonHidden = button.hidden;
        state.buttonAriaHidden = button.getAttribute('aria-hidden');
        button.setAttribute('aria-hidden', 'true');
        button.setAttribute('data-tf-wishlist-enhancing-cta', '');
      }

      return;
    }

    enhancingStates.set(card, {
      button: button,
      buttonHidden: button ? button.hidden : false,
      buttonAriaHidden: button ? button.getAttribute('aria-hidden') : null,
      priceDisplay: styleValue
    });

    card.style.setProperty('--ooo-wl-page-product-card-price-display', 'none');

    if (button) {
      button.setAttribute('aria-hidden', 'true');
      button.setAttribute('data-tf-wishlist-enhancing-cta', '');
    }
  }

  function restoreEnhancingState(card) {
    var state = enhancingStates.get(card);

    if (!state) {
      return;
    }

    if (state.button) {
      if (state.buttonAriaHidden === null) {
        state.button.removeAttribute('aria-hidden');
      } else {
        state.button.setAttribute('aria-hidden', state.buttonAriaHidden);
      }

      state.button.removeAttribute('data-tf-wishlist-enhancing-cta');
      state.button.hidden = state.buttonHidden;
    }

    if (state.priceDisplay) {
      card.style.setProperty('--ooo-wl-page-product-card-price-display', state.priceDisplay);
    } else {
      card.style.removeProperty('--ooo-wl-page-product-card-price-display');
    }

    enhancingStates.delete(card);
  }

  function completeEnhancingState(card, facts) {
    var state = enhancingStates.get(card);

    if (!state) {
      return;
    }

    if (state.button) {
      state.button.removeAttribute('data-tf-wishlist-enhancing-cta');

      if (facts.available && !state.button.disabled) {
        state.button.hidden = state.buttonHidden;

        if (state.buttonAriaHidden === null) {
          state.button.removeAttribute('aria-hidden');
        } else {
          state.button.setAttribute('aria-hidden', state.buttonAriaHidden);
        }
      }
    }

    enhancingStates.delete(card);
  }

  function appendAvailabilityBadge(context, facts) {
    var badgeList;
    var badge;
    var badgeClass = availabilityBadgeClasses[facts.availabilityState];

    removeAvailabilityBadge(context.root, context.productLink);

    if (!badgeClass) {
      return;
    }

    badgeList = global.document.createElement('div');
    badge = global.document.createElement('span');
    badgeList.setAttribute('data-tf-wishlist-availability-badge', '');
    badgeList.className = 'badge-list badge-list--vertical badge-list--collection-page';
    badge.className = 'badge badge--collection-page ' + badgeClass;
    badge.textContent = availabilityLabels[facts.availabilityState];
    badgeList.appendChild(badge);
    context.productLink.setAttribute('data-tf-wishlist-image-link', '');
    context.productLink.appendChild(badgeList);
  }

  function createEnhancement(facts) {
    var pricing = global.TheFaithPresetBundlePricing;
    var priceText = pricing && typeof pricing.formatMoney === 'function' ? pricing.formatMoney(facts.priceCents) : null;
    var priceData;
    var panel;
    var price;
    var bundlePrice;
    var effectivePrice;
    var effectivePriceText;
    var parentPrice;
    var setInfo;

    if (!priceText) {
      return null;
    }

    panel = global.document.createElement('div');
    panel.setAttribute('data-tf-wishlist-merchandising', '');

    price = global.document.createElement('price-list');
    price.setAttribute('data-tf-wishlist-price', '');
    price.className = 'price-list';

    if (
      facts.presetBundle.discountEligible &&
      Number.isInteger(facts.presetBundle.pieceCount) &&
      facts.presetBundle.pieceCount > 0 &&
      pricing &&
      typeof pricing.getEffectivePriceData === 'function'
    ) {
      priceData = pricing.getEffectivePriceData(String(facts.presetBundle.pieceCount), facts.priceCents);
    }

    if (priceData && typeof pricing.formatMoney === 'function') {
      effectivePriceText = pricing.formatMoney(priceData.effectivePriceCents);
    }

    if (priceData && effectivePriceText) {
      bundlePrice = global.document.createElement('span');
      effectivePrice = global.document.createElement('span');
      parentPrice = global.document.createElement('span');
      setInfo = global.document.createElement('span');
      bundlePrice.setAttribute('data-tf-wishlist-bundle-price', '');
      effectivePrice.setAttribute('data-tf-wishlist-effective-price', '');
      parentPrice.setAttribute('data-tf-wishlist-parent-price', '');
      setInfo.setAttribute('data-tf-wishlist-set-info', '');
      bundlePrice.className = 'tf-preset-bundle-price';
      parentPrice.className = 'tf-preset-bundle-price__parent';
      effectivePrice.textContent = effectivePriceText;
      parentPrice.textContent = priceText;
      bundlePrice.appendChild(effectivePrice);
      bundlePrice.appendChild(parentPrice);
      price.appendChild(bundlePrice);
    } else {
      parentPrice = global.document.createElement('sale-price');
      parentPrice.textContent = priceText;
      price.appendChild(parentPrice);
    }

    panel.appendChild(price);
    setInfo = setInfo || global.document.createElement('span');
    setInfo.setAttribute('data-tf-wishlist-set-info', '');

    if (facts.presetBundle.isPresetBundle) {
      setInfo.textContent = facts.presetBundle.pieceCount + '-piece set';

      if (priceData && effectivePriceText) {
        setInfo.textContent += ' · Save ' + priceData.percentage + '%';
      }
    }

    panel.appendChild(setInfo);
    return panel;
  }

  function suppressUnavailableCta(root, facts) {
    var button = root.querySelector('[data-field="form-button"]');

    if (!button || (facts.available && !button.disabled)) {
      return;
    }

    button.hidden = true;
    button.setAttribute('aria-hidden', 'true');
    button.setAttribute('data-tf-wishlist-unavailable-cta', '');
  }

  function resetCard(card, root) {
    var panel = root.querySelector('[data-tf-wishlist-merchandising]');
    var button = root.querySelector('[data-tf-wishlist-unavailable-cta]');

    restoreEnhancingState(card);
    clearCtaHierarchy(root);

    if (panel) {
      panel.remove();
    }

    removeAvailabilityBadge(root, root.querySelector('a[data-tf-wishlist-image-link]'));
    removeTitleSlot(root);

    if (button) {
      button.hidden = false;
      button.removeAttribute('aria-hidden');
      button.removeAttribute('data-tf-wishlist-unavailable-cta');
    }

    card.style.removeProperty('--ooo-wl-page-product-card-price-display');
    card.removeAttribute('data-tf-wishlist-product-key');
    card.removeAttribute('data-tf-wishlist-pending-key');
  }

  function prepareCard(card) {
    var context = getCardContext(card);
    var existingKey;
    var pendingKey;

    if (!context) {
      return null;
    }

    existingKey = card.getAttribute('data-tf-wishlist-product-key');

    if (existingKey && existingKey !== context.key) {
      resetCard(card, context.root);
    }

    if (existingKey === context.key && context.root.querySelector('[data-tf-wishlist-merchandising]')) {
      return null;
    }

    pendingKey = card.getAttribute('data-tf-wishlist-pending-key');

    if (pendingKey === context.key) {
      injectStyles(context.root);
      beginEnhancing(card, context.root);
      return null;
    }

    injectStyles(context.root);
    beginEnhancing(card, context.root);
    card.setAttribute('data-tf-wishlist-product-key', context.key);
    card.setAttribute('data-tf-wishlist-pending-key', context.key);

    return context;
  }

  function processPreparedCard(card, context) {

    return fetchFacts(context).then(function (facts) {
      var panel;
      var purchaseButton;
      var purchaseForm;
      var titleLink;

      if (card.getAttribute('data-tf-wishlist-pending-key') !== context.key) {
        return;
      }

      card.removeAttribute('data-tf-wishlist-pending-key');

      if (!facts || card.getAttribute('data-tf-wishlist-product-key') !== context.key) {
        restoreEnhancingState(card);
        card.removeAttribute('data-tf-wishlist-product-key');
        return;
      }

      panel = createEnhancement(facts);

      if (!panel) {
        restoreEnhancingState(card);
        card.removeAttribute('data-tf-wishlist-product-key');
        return;
      }

      appendAvailabilityBadge(context, facts);
      titleLink = applyTitleSlot(context);
      purchaseButton = context.root.querySelector('[data-field="form-button"]');
      purchaseForm = facts.available && purchaseButton && !purchaseButton.disabled ? getPurchaseForm(purchaseButton) : null;
      markCtaHierarchy(purchaseForm);
      insertAfterTitle(panel, titleLink, context.root);
      positionCtaForm(panel, purchaseForm);

      completeEnhancingState(card, facts);
      suppressUnavailableCta(context.root, facts);
    });
  }

  function enhanceCard(card) {
    var context = prepareCard(card);

    if (!context) {
      return Promise.resolve();
    }

    return processPreparedCard(card, context);
  }

  function runWithConcurrency(preparedCards) {
    var index = 0;
    var workers = [];
    var workerCount = Math.min(MAX_CONCURRENT_FACT_REQUESTS, preparedCards.length);

    function worker() {
      var card;

      if (index >= preparedCards.length) {
        return Promise.resolve();
      }

      card = preparedCards[index];
      index += 1;

      return processPreparedCard(card.card, card.context).then(worker, worker);
    }

    for (var workerIndex = 0; workerIndex < workerCount; workerIndex += 1) {
      workers.push(worker());
    }

    return Promise.all(workers);
  }

  function scan(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll(CARD_SELECTOR));
    var preparedCards = [];
    var context;

    for (var index = 0; index < cards.length; index += 1) {
      context = prepareCard(cards[index]);

      if (context) {
        preparedCards.push({ card: cards[index], context: context });
      }
    }

    if (preparedCards.length) {
      runWithConcurrency(preparedCards);
    }
  }

  function observePageContainer(container) {
    var root = container.shadowRoot;
    var observer;

    if (!root || observedRoots.has(root) || !global.MutationObserver) {
      return;
    }

    observedRoots.add(root);
    scan(root);
    observer = new global.MutationObserver(function () {
      scan(root);
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['pid'] });
  }

  function discoverPageContainers() {
    var containers = global.document.querySelectorAll(PAGE_SELECTOR);

    for (var index = 0; index < containers.length; index += 1) {
      observePageContainer(containers[index]);
    }
  }

  function init() {
    var observer;

    if (!global.document) {
      return;
    }

    discoverPageContainers();

    if (!global.MutationObserver || !global.document.documentElement) {
      return;
    }

    observer = new global.MutationObserver(discoverPageContainers);
    observer.observe(global.document.documentElement, { childList: true, subtree: true });
  }

  global.TheFaithWishlistMerchandising = {
    enhanceCard: enhanceCard,
    scan: scan
  };

  init();
})(typeof window !== 'undefined' ? window : globalThis);
